import { useEffect, useRef, useState } from "react"
import cn from "classnames"
import CallToAction from "components/CallToAction"
import { useRequestSending } from "utilities/useRequestSending"

export function CardListSection({
  Card,
  backgroundColor = "white",
  items,
  total,
  perPage,
  currentPage,
  archiveOrder,
  category,
  stateFilter,
  columns,
  handler,
  allItems,
}) {
  const hasPreloadedItems = Array.isArray(allItems)
  const initialItems = hasPreloadedItems ? allItems.slice(0, currentPage * perPage) : Array.isArray(items) ? items : []
  const initialTotal = hasPreloadedItems ? allItems.length : total

  const [itemList, setItemList] = useState(initialItems)
  const [page, setPage] = useState(currentPage)
  const [isExhausted, setIsExhausted] = useState(initialItems.length >= initialTotal)
  const isLoadingMore = useRef(false)
  const isEnd = isExhausted

  useEffect(() => {
    const nextInitialItems = hasPreloadedItems ? allItems.slice(0, currentPage * perPage) : Array.isArray(items) ? items : []
    const nextTotal = hasPreloadedItems ? allItems.length : total

    setItemList(nextInitialItems)
    setPage(currentPage)
    setIsExhausted(nextInitialItems.length >= nextTotal)
    isLoadingMore.current = false
  }, [allItems, currentPage, hasPreloadedItems, items, perPage, total])

  const { send, isSending } = useRequestSending(async (requestedPage) => handler({
    page: requestedPage,
    perPage,
    category,
    archiveOrder,
    state: stateFilter,
  }),
  )

  const loadMore = async () => {
    if (isSending || isLoadingMore.current || isEnd) return

    if (hasPreloadedItems) {
      const nextPage = page + 1
      const nextItems = allItems.slice(0, nextPage * perPage)

      setItemList(nextItems)
      setPage(nextPage)
      setIsExhausted(nextItems.length >= allItems.length)
      return
    }

    if (!handler) {
      setIsExhausted(true)
      return
    }

    isLoadingMore.current = true
    try {
      const nextPage = page + 1
      const response = await send(nextPage)

      if (!response) {
        return
      }

      const responseItems = Array.isArray(response.items) ? response.items : []
      const hasMore = resolveHasMore({
        response,
        requestedPage: nextPage,
        fallbackPerPage: perPage,
        itemCount: responseItems.length,
      })

      const mergedItems = mergeUniqueItems(itemList, responseItems)

      if (mergedItems.length > itemList.length) {
        setItemList(mergedItems)
      }

      setPage(nextPage)

      if (responseItems.length === 0 || !hasMore) {
        setIsExhausted(true)
      }
    } finally {
      isLoadingMore.current = false
    }
  }

  return <section className={`px-5 bg-${backgroundColor}`}>
    <div className="max-w-screen-xl mx-auto flex flex-col justify-center items-center pb-8 w-full">
      <div
        className={cn("grid sm:grid-cols-2 gap-8 py-8 w-full", {
          "lg:grid-cols-4": columns === 4,
          "lg:grid-cols-3": columns === 3,
        })}
      >
        {itemList?.map((item) => (
          <Card {...item} key={item.name + (item.slug || '')} />
        ))}
      </div>
      {!isEnd && (
        <CallToAction
          style="ghost"
          button
          disabled={isSending}
          loading={isSending}
          onClick={loadMore}
        >
          Load More
        </CallToAction>
      )}
    </div>
  </section>
}

function mergeUniqueItems(currentItems = [], nextItems = []) {
  const existingItems = new Set(currentItems.map(getItemKey).filter(Boolean))

  const uniqueNewItems = nextItems.filter((item) => {
    const key = getItemKey(item)

    if (!key) {
      return true
    }

    if (existingItems.has(key)) {
      return false
    }

    existingItems.add(key)
    return true
  })

  return [...currentItems, ...uniqueNewItems]
}

function getItemKey(item) {
  return item?.uuid || item?.slug || item?._uid || item?.name || null
}

function resolveHasMore({ response, requestedPage, fallbackPerPage, itemCount }) {
  if (typeof response?.hasMore === "boolean") {
    return response.hasMore
  }

  const resolvedTotal = Number(response?.total)
  const resolvedPage = Number(response?.currentPage ?? requestedPage)
  const resolvedPerPage = Number(response?.perPage ?? fallbackPerPage)

  if (
    Number.isFinite(resolvedTotal) &&
    Number.isFinite(resolvedPage) &&
    Number.isFinite(resolvedPerPage)
  ) {
    return resolvedPage * resolvedPerPage < resolvedTotal
  }

  return resolvedPerPage > 0 ? itemCount === resolvedPerPage : false
}
